import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { ActivityIndicator, DeviceEventEmitter, FlatList, KeyboardAvoidingView, Platform, RefreshControl, TouchableOpacity, View, useWindowDimensions } from "react-native";
import moment from "moment";
import Popover from "react-native-popover-view/dist/Popover";
import * as Yup from "yup";
import { ThemeConsumer, useTheme } from "@rneui/themed";

import { config } from "../environment";
import { getScreenPermission, useAppDispatch, useAppSelector } from "../../redux/hooks";
import { shallowEqual } from 'react-redux';
import { appColors, appFonts, fontSize } from "../../themes/appTheme";
import CommonHeader from "./commonHeader";
import CommonDialogBox from "./commonDialogBox";
import CommonService from "../services/commonService";
import { ListToolbar } from "./listToolbar";
import { useAppNavigation, useAppRoute } from "../../types/@react-navigation/navigation";

// Redux Slices
import { bulkDeleteDiscountData, getAllPosDiscountList, getPosDiscountTypes, singleDeleteData, updateDiscountListStatus } from "../../redux/slices/manualDiscountSlice";
import { bulkDeleteDiscount, getAllPosDiscount, updateDiscountStatus } from "../../redux/slices/automaticDiscountSlice";
import { buyXgetYBulkDeleteDiscount, buyXgetYUpdateDiscountStatus, getBuyXgetYDiscounts } from "../../redux/slices/buyXgetYDiscountSlice";
import { bulkDeletePayXgetFreeDiscount, getPayXGetFreePosDiscount, singlePayXGetFreeDelete, updatePayXGetFreeDiscountStatus } from "../../redux/slices/payXGetFreeSlice";

// Core Elements
import ZenText from "zen-core-elements/src/elements/zenText/zenText";
import { ZenBadge, ZenCheckBox, ZenDivider, ZenEmptyComponent, ZenFilterModal, ZenIcon, ZenLoader, ZenToast } from "zen-core-elements";
import { filterItemsProps } from "zen-core-elements/src/elements/zenFilterModal/zenFilterType.modal";

export type DiscountModuleType = 'Manual' | 'Automatic' | 'BuyXGetY' | 'PayXGetFree';

export interface CommonDiscountProps {
    type: DiscountModuleType;
}

const formikValidation = Yup.object().shape({
    from: Yup.number()
        .typeError("Enter valid Input")
        .min(0, "From range should start from 0")
        .nullable()
        .transform((value, originalValue) => (originalValue?.trim() === "" ? null : value)),
    to: Yup.number()
        .typeError("Enter valid Input")
        .min(1, "To range should start from 1")
        .max(100000, "To range should not exceed 100000")
        .nullable()
        .transform((value, originalValue) => (originalValue?.trim() === "" ? null : value))
        .when("from", (from: any, schema: any) => {
            return from && from[0]
                ? schema.moreThan(from[0], "To range must be greater than From range").required("This field is required")
                : schema.test("", "From is required", (to: any) => (!from[0] && to ? false : true));
        }),
});

const CommonDiscountList: FC<CommonDiscountProps> = ({ type }) => {
    const route = useAppRoute("BuyXgetYDiscount");
    // const isWrite = route?.params?.isWrite ?? true;
    const { width, height } = useWindowDimensions();
    const isLandscape = width > height;
    const navigation = useAppNavigation();
    const dispatch = useAppDispatch();
    const { isWrite } = useAppSelector(getScreenPermission(type === "Manual" ? "ManualDiscountStack" : type === "Automatic" ? "AutomaticDiscountStack" : type === "BuyXGetY" ? "BuyXgetYDiscountStack" : "PayXGetFreeStack"), shallowEqual);

    // Module-specific state helpers
    const [discTypes, setDiscTypes] = useState<any[]>([]);
    const [search, setSearch] = useState<string | null>(null);
    const [refresh, setRefresh] = useState<boolean>(false);
    const [offset, setOffset] = useState<number | null>(null);
    const [selectedCards, setSelectedCards] = useState<Set<number>>(new Set([]));
    const [footerLoader, setFooterLoader] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [showPopover, setShowPopover] = useState<boolean>(false);
    const [popoverAction, setPopoverAction] = useState<string>(''); // 'delete' | 'status' | 'bulkDelete'
    const [selectedElement, setSelectedElement] = useState<any>(null);
    const [filterVisible, setFilterVisible] = useState<boolean>(false);
    const [filterCount, setFilterCount] = useState<number>(0);
    const [filterPayload, setFilterPayload] = useState<any>(null);
    const [refinedFilter, setRefinedFilter] = useState<any>(null);

    const onMomentumScrollBegin = useRef(false);
    const limit = 10;

    // Direct Primitive Slices from Redux State (Avoid returning object literal in selector)
    const manualDiscountData = useAppSelector((state) => state?.manualDiscount?.discountList);
    const automaticDiscountData = useAppSelector((state) => state?.automaticDiscount?.discountVal);
    const buyXgetYDiscountData = useAppSelector((state) => state?.buyXgetYDiscount?.discountVal);
    const payXDiscountData = useAppSelector((state) => state?.payXDiscount?.payXDiscountData);
    const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'

    const reduxRows = useMemo(() => {
        switch (type) {
            case "Manual":
                return manualDiscountData?.value || [];
            case "Automatic":
                return automaticDiscountData?.value || [];
            case "BuyXGetY":
                return buyXgetYDiscountData?.discountList?.rows || [];
            case "PayXGetFree":
                return payXDiscountData?.discountList?.rows || [];
            default:
                return [];
        }
    }, [type, manualDiscountData, automaticDiscountData, buyXgetYDiscountData, payXDiscountData]);

    const reduxCount = useMemo(() => {
        switch (type) {
            case "Manual":
                return manualDiscountData?.count || 0;
            case "Automatic":
                return automaticDiscountData?.count || 0;
            case "BuyXGetY":
                return buyXgetYDiscountData?.discountList?.count || 0;
            case "PayXGetFree":
                return payXDiscountData?.discountList?.count || 0;
            default:
                return 0;
        }
    }, [type, manualDiscountData, automaticDiscountData, buyXgetYDiscountData, payXDiscountData]);

    const [localData, setLocalData] = useState<any[]>(reduxRows);

    // Filter Items Configuration with Unique Keys per Module
    const getInitialFilterItems = (): filterItemsProps[] => {
        switch (type) {
            case "Manual":
                return [
                    { id: "manual_type", filterName: "Type", filterType: "radio", filterValue: [{ value: "Percentage", label: "Percentage" }, { value: "Fixed", label: "Fixed" }], initialSelected: "" },
                    { id: "auto_range", filterName: "Discount_Range", filterType: "inputRange", initialSelected: { from: "", to: "" }, validation: formikValidation },
                    // { id: "manual_range", filterName: "Discount Range", filterType: "inputRange", filterValue: { from: "", to: "" }, initialSelected: { from: "", to: "" } },
                    { id: "manual_date", filterName: "CustomDate", filterType: "dateRange", filterValue: { minDate: "", maxDate: "" }, initialSelected: { startDate: "", endDate: "" } }
                ];
            case "Automatic":
                return [
                    { id: "auto_type", filterName: "Type", filterType: "radio", filterValue: [{ value: "Fixed", label: "Fixed" }, { value: "Percentage", label: "Percentage" }], initialSelected: "" },
                    { id: "auto_range", filterName: "Discount_Range", filterType: "inputRange", initialSelected: { from: "", to: "" }, validation: formikValidation },
                    { id: "auto_date", filterName: "CustomDate", filterType: "dateRange", filterValue: { minDate: "", maxDate: "" }, initialSelected: { startDate: "", endDate: "" } }
                ];
            case "BuyXGetY":
                return [
                    { id: "buyx_medium", filterName: "Medium", filterType: "radio", initialSelected: "", filterValue: [{ value: "POS", label: "POS" }, { value: "WEB", label: "WEB" }] },
                    { id: "buyx_avail", filterName: "Availability", filterType: "radio", initialSelected: "", filterValue: [{ value: "POS", label: "POS" }, { value: "BOTH", label: "BOTH" }] },
                    { id: "buyx_date", filterName: "CustomDate", filterType: "dateRange", initialSelected: { startDate: "", endDate: "" }, filterValue: { minDate: "", maxDate: "" } }
                ];
            case "PayXGetFree":
                return [
                    { id: "payx_medium", filterName: "Medium", filterType: "radio", filterValue: [{ value: "POS", label: "POS" }, { value: "WEB", label: "WEB" }], initialSelected: "" },
                    { id: "payx_vis", filterName: "Availability", filterType: "radio", filterValue: [{ value: "POS", label: "POS" }, { value: "Both", label: "Both" }], initialSelected: "" },
                    { id: "payx_date", filterName: "CustomDate", filterType: "dateRange", filterValue: { minDate: "", maxDate: "" }, initialSelected: { startDate: "", endDate: "" } }
                ];
        }
    };

    const [filterData, setFilterData] = useState<filterItemsProps[]>(getInitialFilterItems());

    // Update filter items when type changes
    useEffect(() => {
        setFilterData(getInitialFilterItems());
        setFilterCount(0);
        setFilterPayload(null);
        setRefinedFilter(null);
    }, [type]);

    // Clear selections when screen is focused
    // useEffect(() => {
    //     if (isFocused) {
    //         setSelectedCards(new Set([]));
    //     }
    // }, [isFocused]);

    // Config metadata according to Discount Module Type
    const getModuleConfig = () => {
        switch (type) {
            case "Manual":
                return {
                    headerName: "Manual Discount",
                    addRoute: "AddEditManual" as const,
                    editRoute: "AddEditManual" as const,
                    getEditParams: (id: number) => ({ discountId: id }),
                    viewRouteName: "Manual" as const,
                    eventEmitterName: "MANUAL_DISCOUNT_UPDATED",
                    filterEventEmitterName: "manualDiscountListFilter",
                    filterName: "manualDiscountListFilter",
                    searchPlaceholder: "Search by promo code, discount value ",
                };
            case "Automatic":
                return {
                    headerName: "Automatic Discount",
                    addRoute: "AddEditAutomaticDiscount" as const,
                    editRoute: "AddEditAutomaticDiscount" as const,
                    getEditParams: (id: number) => ({ id }),
                    viewRouteName: "Automatic" as const,
                    eventEmitterName: "AUTOMATIC_DISCOUNT_UPDATED",
                    filterEventEmitterName: "automaticDiscountListFilter",
                    filterName: "automaticDiscountListFilter",
                    searchPlaceholder: "Search by discount name, discount value ",
                };
            case "BuyXGetY":
                return {
                    headerName: "Buy X Get Y Discounts",
                    addRoute: "AddEditBuyXgetYDiscount" as const,
                    editRoute: "AddEditBuyXgetYDiscount" as const,
                    getEditParams: (id: number) => ({ id }),
                    viewRouteName: "BuyXgetY" as const,
                    eventEmitterName: "BUYXGETY_UPDATED",
                    filterEventEmitterName: "buyxDiscountList",
                    filterName: "buyxDiscountList",
                    searchPlaceholder: "Search by discount title",
                };
            case "PayXGetFree":
                return {
                    headerName: "Pay X Get Free Discount",
                    addRoute: "AddEditPayXGetFree" as const,
                    editRoute: "AddEditPayXGetFree" as const,
                    getEditParams: (id: number) => ({ discountId: id }),
                    viewRouteName: "PayXGetFree" as const,
                    eventEmitterName: "PAYXGETFREE_UPDATED",
                    filterEventEmitterName: "payxDiscountList",
                    filterName: "payxDiscountList",
                    searchPlaceholder: "Search by discount title & purchase amount.",
                };
        }
    };

    const moduleConfig = getModuleConfig();

    // Reinitialize state helper
    const reInitializeStates = () => {
        onMomentumScrollBegin.current = false;
        DeviceEventEmitter?.emit(moduleConfig.filterEventEmitterName);
        setRefinedFilter(null);
        setFilterPayload(null);
        setSearch(null);
        setSelectedCards(new Set([]));
    };

    // Event Listener for updates
    useEffect(() => {
        const listener = DeviceEventEmitter.addListener(moduleConfig.eventEmitterName, () => {
            reInitializeStates();
        });
        return () => {
            listener?.remove();
        };
    }, [moduleConfig.eventEmitterName]);

    // Manual Discount Types Fetch (needed for filter type matching)
    useEffect(() => {
        if (type === "Manual") {
            dispatch(getPosDiscountTypes({ storeId: config?.storeId }))
                .unwrap()
                .then((res: any) => {
                    if (res && res?.discountTypes) {
                        setDiscTypes(res.discountTypes);
                    }
                })
                .catch((err: any) => console.log("Error in getDiscountTypes", err));
        }
    }, [type]);

    // Handle offset toggle helper
    const toggleOffset = () => {
        onMomentumScrollBegin.current = false;
        setOffset((prev) => (prev === 0 ? -1 : 0));
    };

    useEffect(() => {
        toggleOffset();
    }, []);

    // Sync Redux rows to local state
    useEffect(() => {
        if (reduxRows) {
            setLocalData(reduxRows);
        }
    }, [reduxRows]);

    // Handle Search Debounce
    useEffect(() => {
        if (search !== null) {
            setLoading(true);
            onMomentumScrollBegin.current = false;
            const debounceTimer = setTimeout(() => {
                toggleOffset();
            }, 500);
            return () => clearTimeout(debounceTimer);
        }
    }, [search]);

    // Handle Filter Payload Refinement
    useEffect(() => {
        if (filterPayload !== null) {

            setLoading(true);
            if (type === "Manual" || type === "Automatic") {
                let typeData: any;
                const startDate = filterPayload?.CustomDate?.startDate;
                const endDate = filterPayload?.CustomDate?.endDate;
                const range = filterPayload?.["inputRange"] || filterPayload?.["Discount_Range"] || filterPayload?.["Discount Range"];
                const from = range?.from;
                const to = range?.to;
                const selectedType = filterPayload?.Type || filterPayload?.type;
                if (selectedType) {
                    if (type === "Manual") {
                        typeData = discTypes?.find((e: any) => e.discountName === selectedType)?.id;
                    } else {
                        typeData = selectedType === "Fixed" ? "1" : "2";
                    }
                }
                setRefinedFilter({
                    type: typeData,
                    discountRange: {
                        from: from ?? null,
                        to: to ?? null,
                    },
                    customDateRange: {
                        begin: startDate ? moment(startDate).format("YYYY-MM-DD") : null,
                        end: endDate ? moment(endDate).format("YYYY-MM-DD") : null,
                    },
                });
            } else if (type === "BuyXGetY" || type === "PayXGetFree") {
                const startDate = filterPayload?.CustomDate?.startDate;
                const endDate = filterPayload?.CustomDate?.endDate;
                const medium = filterPayload?.Medium || filterPayload?.medium;
                const visibility = filterPayload?.Availability || filterPayload?.Visibility || filterPayload?.visibility;
                setRefinedFilter({
                    medium: medium,
                    visibility: visibility,
                    customDateRange: {
                        begin: startDate ? moment(startDate).format("YYYY-MM-DD") : null,
                        end: endDate ? moment(endDate).format("YYYY-MM-DD") : null,
                    },
                });
            }
            toggleOffset();
        }
    }, [filterPayload]);

    // Fetch Discount List Action
    const fetchDiscountList = () => {
        if (!refresh && !footerLoader) {
            setLoading(true);
        }
        const payloadData = {
            offset: offset === -1 ? 0 : offset || 0,
            limit: limit,
            filterData: refinedFilter ? JSON.stringify(refinedFilter) : null,
            searchText: search ? search.trim() : "",
            type: type === "BuyXGetY" ? "BuyXGetY" : type === "PayXGetFree" ? "PayXGetFree" : type,
        };
        const props = { storeId: config?.storeId, data: payloadData };

        let actionPromise: any;
        switch (type) {
            case "Manual":
                actionPromise = dispatch(getAllPosDiscountList(props));
                break;
            case "Automatic":
                actionPromise = dispatch(getAllPosDiscount(props));
                break;
            case "BuyXGetY":
                actionPromise = dispatch(getBuyXgetYDiscounts(props));
                break;
            case "PayXGetFree":
                actionPromise = dispatch(getPayXGetFreePosDiscount(props));
                break;
        }

        if (actionPromise) {
            actionPromise
                .unwrap()
                .catch((err: any) => console.log(`Error fetching ${type} discount list`, err))
                .finally(() => {
                    setLoading(false);
                    setRefresh(false);
                    setFooterLoader(false);
                    onMomentumScrollBegin.current = false;
                });
        }
    };

    useEffect(() => {
        if (offset !== null) {
            fetchDiscountList();
        }
    }, [offset]);

    // Action Handlers: Bulk Delete, Single Delete, Status Update
    const executeBulkDelete = () => {
        setLoading(true);
        const ids = Array.from(selectedCards);
        let actionPromise: any;

        if (type === "Manual") {
            actionPromise = dispatch(bulkDeleteDiscountData({ storeId: config?.storeId, selectedItems: ids }));
        } else if (type === "Automatic") {
            actionPromise = dispatch(bulkDeleteDiscount({ storeId: config?.storeId, data: ids }));
        } else if (type === "BuyXGetY") {
            actionPromise = dispatch(buyXgetYBulkDeleteDiscount({ storeId: config?.storeId, data: ids }));
        } else if (type === "PayXGetFree") {
            actionPromise = dispatch(bulkDeletePayXgetFreeDiscount({ storeId: config?.storeId, selectedItems: ids }));
        }

        if (actionPromise) {
            actionPromise
                .unwrap()
                .then((res: any) => {
                    if (res) {
                        ZenToast({ type: "success", message: "Selected discount(s) were removed successfully." });
                        setSelectedCards(new Set([]));
                        toggleOffset();
                    }
                })
                .catch((err: any) => {
                    ZenToast({ type: "error", message: "Failed to remove selected discount(s)." });
                    console.log(`Error in bulkDelete ${type}`, err);
                })
                .finally(() => setLoading(false));
        }
    };

    const executeSingleDeleteOrStatusUpdate = () => {
        setLoading(true);
        let actionPromise: any;

        if (popoverAction === "delete") {
            if (type === "Manual") {
                actionPromise = dispatch(singleDeleteData({ storeId: config?.storeId, data: { isDeleted: true }, id: selectedElement?.id }));
            } else if (type === "Automatic") {
                actionPromise = dispatch(updateDiscountStatus({ storeId: config?.storeId, discountId: selectedElement?.id, data: { isDeleted: true }, action: "delete" }));
            } else if (type === "BuyXGetY") {
                actionPromise = dispatch(buyXgetYUpdateDiscountStatus({ storeId: config?.storeId, discountId: selectedElement?.id, data: { isDeleted: true }, action: "delete" }));
            } else if (type === "PayXGetFree") {
                actionPromise = dispatch(singlePayXGetFreeDelete({ storeId: config?.storeId, data: { isDeleted: true }, id: selectedElement?.id }));
            }
        } else if (popoverAction === "status") {
            const nextStatus = !selectedElement?.isActive;
            if (type === "Manual") {
                actionPromise = dispatch(updateDiscountListStatus({ storeId: config?.storeId, data: { isActive: nextStatus }, id: selectedElement?.id }));
            } else if (type === "Automatic") {
                actionPromise = dispatch(updateDiscountStatus({ storeId: config?.storeId, discountId: selectedElement?.id, data: { isActive: nextStatus }, action: "update" }));
            } else if (type === "BuyXGetY") {
                actionPromise = dispatch(buyXgetYUpdateDiscountStatus({ storeId: config?.storeId, discountId: selectedElement?.id, data: { isActive: nextStatus }, action: "update" }));
            } else if (type === "PayXGetFree") {
                actionPromise = dispatch(updatePayXGetFreeDiscountStatus({ storeId: config?.storeId, data: { isActive: nextStatus }, id: selectedElement?.id }));
            }
        }

        if (actionPromise) {
            actionPromise
                .unwrap()
                .then((res: any) => {
                    if (res) {
                        const msg = popoverAction === "delete"
                            ? `${type === "BuyXGetY" ? "BuyXGetY" : type === "PayXGetFree" ? "Pay X Get Free" : type} discount removed successfully.`
                            : `Discount ${selectedElement?.isActive ? "deactivated" : "activated"} successfully.`;
                        ZenToast({ type: "success", message: msg });
                        setSelectedCards(new Set([]));
                        toggleOffset();
                    }
                })
                .catch((err: any) => console.log(`Error in single action ${type}`, err))
                .finally(() => setLoading(false));
        }
    };

    // Dialog confirmation listener
    const onConfirmation = (val: { name: string }) => {
        setShowPopover(false);
        if (val?.name === "yes" || val?.name === "Yes") {
            if (popoverAction === "bulkDelete") {
                executeBulkDelete();
            } else {
                executeSingleDeleteOrStatusUpdate();
            }
        }
    };

    // Multi-select helpers
    const toggleSelect = (id: number) => {
        setSelectedCards((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleCardPress = (item: any) => {
        if (selectedCards.size > 0 && isWrite) {
            toggleSelect(item.id);
        }
    };

    const handleCardLongPress = (item: any) => {
        if (selectedCards.size === 0 && isWrite && !isLandscape) {
            toggleSelect(item.id);
        }
    };

    // Dialog configuration objects
    const deleteDialogProps = {
        content: selectedCards.size > 0 ? "Are you sure you want to remove the selected discount(s)?" : `Are you sure you want to remove this discount${selectedElement?.title ? ` ${selectedElement.title}` : ""}?`,
        button: [{ id: 1, name: "Yes" }, { id: 2, name: "No" }],
        icon: { name: "delete-outline", color: appColors.red, type: "material-community" }
    };

    const statusDialogProps = {
        content: `Are you sure you want to ${selectedElement?.isActive ? "deactivate" : "activate"} this discount${selectedElement?.title ? ` ${selectedElement.title}` : ""}?`,
        button: [{ id: 1, name: "Yes" }, { id: 2, name: "No" }],
        icon: { name: selectedElement?.isActive ? "close-circle-outline" : "check-circle-outline", color: selectedElement?.isActive ? appColors.red : appColors.green, type: "material-community" }
    };

    // Item value formatters
    const getItemTitle = (item: any) => {
        if (type === "Manual") return item?.promocode || "Discount";
        return item?.title || "Discount Not found";
    };

    const getItemValue = (item: any) => {
        if (type === "Manual" || type === "Automatic") {
            return item.discountType?.discountName === "Fixed"
                ? CommonService.currencyStandard(currencySymbol, item.discountValue?.toString() || "0")
                : `${item.discountValue || 0}%`;
        }
        if (type === "BuyXGetY") {
            return item.getyData?.[0]?.getyPercentage ? `${item.getyData[0].getyPercentage}%` : item.getyData?.[0]?.getyPercentage ?? "-";
        }
        if (type === "PayXGetFree") {
            return item.minValue ? CommonService.currencyStandard(currencySymbol, item.minValue?.toString() || "0") : item.minValue ?? "-";
        }
        return "-";
    };

    const getItemAvailability = (item: any) => {
        if (type === "Manual" || type === "Automatic") {
            return item.isPos && item.isWeb ? "Both" : item.isPos ? "POS" : "";
        }
        return item.isWeb ? "BOTH" : "POS";
    };

    const getItemDateRange = (item: any) => {
        const start = item?.startDate ? moment(item.startDate).format("DD/MM/YYYY") : "";
        const end = item?.endDate ? moment(item.endDate).format("DD/MM/YYYY") : type === "Manual" || type === "Automatic" ? "N/A" : null;
        return end ? `${start} - ${end}` : start;
    };

    const isExpired = (item: any) => {
        return item.endDate && moment(item.endDate).endOf("day").toDate() < moment(new Date()).endOf("day").toDate();
    };

    const isScheduled = (item: any) => {
        return item.startDate && new Date(item.startDate) > new Date();
    };

    const renderItem = ({ item }: { item: any }) => {
        const isSel = selectedCards?.has(item?.id);
        const expired = isExpired(item);
        const scheduled = isScheduled(item);
        const badgeStatus = !item.isActive ? "InActive" : scheduled ? "Scheduled" : expired ? "Expired" : "Active";
        const badgeColor = !item.isActive ? appColors.inActiveColor : scheduled ? appColors.wildBlue : expired ? appColors.grey : appColors.badgeColor;

        return (
            <ThemeConsumer>
                {({ theme }) => (
                    <TouchableOpacity
                        activeOpacity={isLandscape ? 1 : 0.8}
                        onPress={() => handleCardPress(item)}
                        onLongPress={() => handleCardLongPress(item)}
                    >
                        <View
                            style={{
                                marginBottom: 10,
                                marginHorizontal: 10,
                                padding: 12,
                                borderRadius: 8,
                                borderColor: appColors.primary,
                                borderWidth: isSel && !isLandscape ? 2 : 0,
                                backgroundColor: theme.light,
                                shadowColor: "#000",
                                shadowOffset: { width: 0, height: 1 },
                                shadowOpacity: 0.1,
                                shadowRadius: 2,
                                elevation: 2
                            }}
                        >
                            {/* Card Top Row: Title + Type Badge + Status Badge */}
                            <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                                <View style={{ flexDirection: "row", alignItems: "center", gap: 8, flex: 1 }}>
                                    <ZenText
                                        content={getItemTitle(item)}
                                        numberOfLines={1}
                                        style={{ color: appColors.lightDark, fontFamily: appFonts.bold, fontSize: theme?.smallerFontSize || 15 }}
                                    />
                                    {(type === "Manual" || type === "Automatic") && item?.discountType?.discountName && (
                                        <ZenBadge
                                            badgeColor="#ffedbd"
                                            badgeName={item.discountType.discountName}
                                            badgeTextColor={theme?.lightDark}
                                            badgeTextSize="small"
                                            badgeTextStyle={{ fontFamily: theme?.bold, padding: 2 }}
                                        />
                                    )}
                                </View>

                                <ZenBadge
                                    badgeColor={badgeColor}
                                    badgeName={badgeStatus}
                                    badgeTextColor={theme?.light}
                                    badgeTextSize="small"
                                    badgeTextStyle={{ fontFamily: theme?.bold, padding: 2 }}
                                />
                            </View>

                            <ZenDivider style={{ marginVertical: 8 }} />

                            {/* Card Content Grid / Row */}
                            <View style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
                                {isWrite && isLandscape && (
                                    <View style={{ marginRight: 10 }}>
                                        <ZenCheckBox
                                            checked={isSel}
                                            size={22}
                                            checkedColor={appColors.primary}
                                            onPress={() => toggleSelect(item.id)}
                                        />
                                    </View>
                                )}

                                <View style={{ flex: 0.75, flexDirection: isLandscape ? "row" : "column", rowGap: 8 }}>
                                    {/* Medium */}
                                    <View style={{ flex: 1, flexDirection: isLandscape ? "column" : "row" }}>
                                        <ZenText content={isLandscape ? "Medium" : "Medium : "} style={{ color: appColors.labelColor, fontSize: fontSize.smallFontSize }} />
                                        <ZenText content={item.medium || "-"} numberOfLines={1} bold style={{ color: appColors.lightDark, fontSize: fontSize.smallFontSize }} />
                                    </View>

                                    {/* Availability */}
                                    <View style={{ flex: 1, flexDirection: isLandscape ? "column" : "row" }}>
                                        <ZenText content={isLandscape ? "Availability" : "Availability : "} style={{ color: appColors.labelColor, fontSize: fontSize.smallFontSize }} />
                                        <ZenText content={getItemAvailability(item)} numberOfLines={1} bold style={{ color: appColors.lightDark, fontSize: fontSize.smallFontSize }} />
                                    </View>

                                    {/* Value */}
                                    <View style={{ flex: 1, flexDirection: isLandscape ? "column" : "row" }}>
                                        <ZenText content={isLandscape ? "Value" : "Value : "} style={{ color: appColors.labelColor, fontSize: fontSize.smallFontSize }} />
                                        <ZenText content={getItemValue(item)} numberOfLines={1} bold style={{ color: appColors.lightDark, fontSize: fontSize.smallFontSize }} />
                                    </View>

                                    {/* Available Date */}
                                    <View style={{ flex: 2, flexDirection: isLandscape ? "column" : "row" }}>
                                        <ZenText content={isLandscape ? "Available Date" : "Available Date : "} style={{ color: appColors.labelColor, fontSize: fontSize.smallFontSize }} />
                                        <ZenText content={getItemDateRange(item)} numberOfLines={1} bold style={{ color: appColors.lightDark, fontSize: fontSize.smallFontSize }} />
                                    </View>
                                </View>

                                {/* Action Buttons */}
                                <View style={{ flex: 0.25, flexDirection: isLandscape ? "row" : "column", gap: 10, justifyContent: "flex-end" }}>
                                    <View style={{ flexDirection: "row", gap: 10, justifyContent: "flex-end" }}>

                                        <TouchableOpacity style={{
                                            padding: 8,
                                            borderRadius: 22,
                                            justifyContent: "center",
                                            alignItems: "center",
                                            backgroundColor: theme.lightBackgroundColor,
                                        }}
                                            onPress={() => navigation.navigate(moduleConfig.editRoute as any, moduleConfig.getEditParams(item.id))}>
                                            <ZenIcon
                                                name={isWrite ? "pencil" : "eye"}
                                                type="octicon"
                                                color={theme.iconColor || appColors.primary}
                                                size={theme.iconSize}
                                            />
                                        </TouchableOpacity>
                                        <TouchableOpacity style={{
                                            padding: 8,
                                            borderRadius: 22,
                                            justifyContent: "center",
                                            alignItems: "center",
                                            backgroundColor: theme.lightBackgroundColor,
                                        }}
                                            onPress={() => navigation.navigate("ViewDiscount", { id: item.id, routeName: moduleConfig.viewRouteName })}>

                                            <ZenIcon
                                                name="clock-time-three-outline"
                                                type="material-community"
                                                color={theme.iconColor || appColors.primary}
                                                size={theme.iconSize}
                                            />
                                        </TouchableOpacity>
                                    </View>
                                    <View style={{ flexDirection: "row", gap: 10, justifyContent: "flex-end" }}>

                                        {!expired && isWrite && (
                                            <TouchableOpacity style={{
                                                padding: 8,
                                                borderRadius: 22,
                                                justifyContent: "center",
                                                alignItems: "center",
                                                backgroundColor: theme.lightBackgroundColor,
                                            }}
                                                onPress={() => {
                                                    setSelectedElement(item);
                                                    setPopoverAction("status");
                                                    setShowPopover(true);
                                                }}>

                                                <ZenIcon
                                                    name={item.isActive ? "close-circle-outline" : "check-circle-outline"}
                                                    type="material-community"
                                                    color={item.isActive ? appColors.red : appColors.green}
                                                    size={theme.iconSize || 20}
                                                />
                                            </TouchableOpacity>
                                        )}

                                        {selectedCards.size <= 0 && isWrite && (
                                            <TouchableOpacity style={{
                                                padding: 8,
                                                borderRadius: 22,
                                                justifyContent: "center",
                                                alignItems: "center",
                                                backgroundColor: theme.lightBackgroundColor,
                                            }}
                                                onPress={() => {
                                                    setSelectedElement(item);
                                                    setPopoverAction("delete");
                                                    setShowPopover(true);
                                                }}>
                                                <ZenIcon
                                                    name="delete-outline"
                                                    type="material-community"
                                                    color={appColors.red}
                                                    size={theme.iconSize || 20}
                                                />
                                            </TouchableOpacity>
                                        )}
                                    </View>
                                </View>
                            </View>
                        </View>
                    </TouchableOpacity>
                )}
            </ThemeConsumer>
        );
    };

    const onEndReached = () => {
        if (!onMomentumScrollBegin.current) return;
        if (footerLoader || loading) return;
        const currentOffset = offset && offset !== -1 ? offset : 0;
        if (reduxCount > currentOffset + limit) {
            onMomentumScrollBegin.current = false;
            setFooterLoader(true);
            setOffset(currentOffset + limit);
        }
    };

    const onRefresh = () => {
        setRefresh(true);
        reInitializeStates();
        toggleOffset();
    };

    return (
        <ThemeConsumer>
            {({ theme }) => (
                <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined} style={{ flex: 1, backgroundColor: appColors.lightBackgroundColor }}>
                    <CommonHeader headerName={moduleConfig.headerName} />

                    <View style={{ flex: 1 }}>
                        <ListToolbar
                            search={{
                                value: search || "",
                                onChange: (val) => setSearch(val as string),
                                onCancel: () => setSearch(null),
                                placeholder: moduleConfig.searchPlaceholder,
                            }}
                            filter={{
                                count: filterCount,
                                onPress: () => setFilterVisible(true),
                            }}
                            buttons={
                                isWrite
                                    ? [
                                        {
                                            title: "Add Discount",
                                            onPress: () => navigation.navigate(moduleConfig.addRoute as any),
                                        },
                                    ]
                                    : []
                            }
                        />

                        {/* Multi-Select Toolbar Header */}
                        {selectedCards.size > 0 && (
                            <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginHorizontal: 10, marginVertical: 6 }}>
                                <ZenCheckBox
                                    title={selectedCards.size === localData.length ? "Deselect All" : "Select All"}
                                    containerStyle={{ margin: 0, padding: 0, backgroundColor: "transparent" }}
                                    titleStyle={{ fontSize: theme?.mediumFontSize }}
                                    checkedColor={theme.primary}
                                    size={20}
                                    checked={selectedCards.size === localData.length}
                                    onPress={() => {
                                        if (selectedCards.size === localData.length) {
                                            setSelectedCards(new Set());
                                        } else {
                                            const allIds = new Set<number>();
                                            localData.forEach((card) => allIds.add(card.id));
                                            setSelectedCards(allIds);
                                        }
                                    }}
                                />

                                {isWrite && (

                                    <TouchableOpacity
                                        style={{
                                            borderRadius: 7,
                                            paddingVertical: 9,
                                            paddingHorizontal: 14,
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            backgroundColor: theme?.red,
                                            marginVertical: 5
                                        }}
                                        onPress={() => {
                                            setPopoverAction("bulkDelete");
                                            setShowPopover(true);
                                        }}>
                                        <ZenText content={"Delete"} bold size={theme?.smallFontSize} color={theme?.light} />
                                    </TouchableOpacity>

                                )}
                            </View>
                        )}

                        {/* Main Content List / Loader */}
                        {loading && !refresh && !footerLoader ? (
                            <ZenLoader lottie={require("../../assets/infinityLoader.json")} lottieType="Local" viewStyle={{ backgroundColor: "transparent" }} />
                        ) : (
                            <FlatList
                                data={localData}
                                renderItem={renderItem}
                                keyExtractor={(item, index) => `${type}_${item?.id ?? index}_${index}`}
                                onMomentumScrollBegin={() => {
                                    onMomentumScrollBegin.current = true;
                                }}
                                onEndReached={onEndReached}
                                onEndReachedThreshold={0.5}
                                refreshControl={<RefreshControl refreshing={refresh} onRefresh={onRefresh} />}
                                ListEmptyComponent={<ZenEmptyComponent viewStyle={{ backgroundColor: theme?.light }} />}
                                ListFooterComponent={
                                    footerLoader ? (
                                        <View style={{ paddingVertical: 10 }}>
                                            <ActivityIndicator size="small" color={theme?.primary} />
                                        </View>
                                    ) : null
                                }
                                contentContainerStyle={{
                                    paddingBottom: localData?.length ? 20 : undefined,
                                    flexGrow: localData?.length ? undefined : 1,
                                }}
                            />
                        )}
                    </View>

                    {/* Filter Modal */}
                    <ZenFilterModal
                        filterName={moduleConfig.filterName}
                        filterItems={filterData}
                        setFilterItems={setFilterData}
                        visible={filterVisible}
                        setVisible={setFilterVisible}
                        filterCount={filterCount}
                        setFilterCount={setFilterCount}
                        title={`${moduleConfig.headerName} Filter`}
                        onConfirm={(val: any) => {
                            setSelectedCards(new Set([]));
                            console.log(val, "==================");

                            setFilterPayload(val?.values || val);
                        }}
                    />

                    {/* FAB Button for Mobile Viewport */}
                    {!isLandscape && isWrite && (
                        <View style={{ position: "absolute", bottom: 20, right: 20 }}>
                            {selectedCards.size === 0 ? (
                                <TouchableOpacity
                                    style={{
                                        width: 54,
                                        height: 54,
                                        borderRadius: 27,
                                        backgroundColor: appColors.primary,
                                        justifyContent: "center",
                                        alignItems: "center",
                                        elevation: 5,
                                    }}
                                    onPress={() => navigation.navigate(moduleConfig.addRoute as any)}
                                >
                                    <ZenIcon name="plus" type="material-community" size={28} color={appColors.light} />
                                </TouchableOpacity>
                            ) : (
                                <TouchableOpacity
                                    style={{
                                        width: 54,
                                        height: 54,
                                        borderRadius: 27,
                                        backgroundColor: appColors.red,
                                        justifyContent: "center",
                                        alignItems: "center",
                                        elevation: 5,
                                    }}
                                    onPress={() => {
                                        setPopoverAction("bulkDelete");
                                        setShowPopover(true);
                                    }}
                                >
                                    <ZenIcon name="delete-outline" type="material-community" size={28} color={appColors.light} />
                                </TouchableOpacity>
                            )}
                        </View>
                    )}

                    {/* Dialog Confirmation Popover */}
                    <Popover
                        isVisible={showPopover}
                        onRequestClose={() => setShowPopover(false)}
                        popoverStyle={{ width: width > 900 ? 500 : width > 600 ? 450 : 350, borderRadius: 10 }}
                    >
                        <CommonDialogBox
                            type="OTHER"
                            dialog={popoverAction === "status" ? statusDialogProps : deleteDialogProps}
                            confirmation={onConfirmation}
                        />
                    </Popover>
                </KeyboardAvoidingView>
            )}
        </ThemeConsumer>
    );
};

export default CommonDiscountList;
